<template>
    <div
        class="signature-plan"
        :class="{
            'rounded-b-lg': selectedPlanType !== LicenseType.SIGNATURE,
            '!border-b-0': selectedPlanType === LicenseType.SIGNATURE,
            disabled: isMonthlyPlan,
        }"
        @click="selectPlan"
    >
        <div class="left-section-sig">
            <div class="left-section-header">
                <PrimeRadioButton
                    @click="selectPlan"
                    v-model="selectedPlanType"
                    name="Signature"
                    :value="LicenseType.SIGNATURE"
                    :disabled="isMonthlyPlan"
                />
                <div class="license-title-container">
                    <div class="license-name-price-container">
                        <div class="flex items-center">
                            <span>Signature</span>
                            <span v-if="purchaseType === PurchaseType.GIFT && availableLicenses > 0" class="available-licenses-badge">
                                {{ availableLicenses }}
                            </span>
                        </div>
                        <div class="lg:hidden flex flex-col items-end" v-if="!store.isLoadingLicenseCosts && !(purchaseType === PurchaseType.GIFT && availableLicenses > 0)">
                            <span class="price-text">
                                {{ priceParts.dollars }}<span v-if="priceParts.cents !== null"><sup class="cents-sup">{{ priceParts.cents }}</sup></span>
                            </span>
                            <span class="user-time-text">User / {{ LicenseRenewalTermDisplay[selectedTerm] }}</span>
                        </div>
                    </div>
                    
                    <span v-if="license?.slug === LicenseType.SIGNATURE && purchaseType === PurchaseType.UPGRADE" class="current-license-badge">Current License</span>
                </div>
                <span v-if="isMonthlyPlan" class="text-zinc-500 text-sm">(Not available monthly)</span>
            </div>
            <p>White-glove expedited priority for individuals in crisis or at high risk</p>
        </div>
        <div class="right-section-sig">
            <span v-if="store.isLoadingLicenseCosts" class="loading-price">
                <LoadingDots />
            </span>
            <span v-else-if="purchaseType === PurchaseType.GIFT && availableLicenses > 0">
                {{ availableLicenses }} Available
            </span>
            <div v-else class="hidden lg:flex flex-col items-end text-right">
                <span class="price-text">
                    {{ priceParts.dollars }}<span v-if="priceParts.cents !== null"><sup class="cents-sup">{{ priceParts.cents }}</sup></span>
                </span>
                <span class="user-time-text">User / {{ LicenseRenewalTermDisplay[selectedTerm] }}</span>
            </div>
        </div>
    </div>

    <div
        class="benefits"
        :class="{
            'rounded-b-lg': selectedPlanType === LicenseType.SIGNATURE,
        }"
        v-if="selectedPlanType === LicenseType.SIGNATURE"
    >
        <div class="benefits-list">
            <div v-for="(benefit, index) in store.benefits.signature" :key="index" class="benefit">
                <CheckCircleIcon class="check-icon" />
                <p>{{ benefit }}</p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useSubscribeStore } from '@/stores/components/modals/subscription-modal';
import { CheckCircleIcon } from '@heroicons/vue/24/solid';
import PrimeRadioButton from 'primevue/radiobutton';
import LoadingDots from '~/components/main/shared/LoadingDots.vue';
import useUtils from '~/composables/useUtils';
import { usePersonStore } from '~/stores/person';
import { LicenseRenewalTerm, LicenseRenewalTermDisplay, LicenseType, PurchaseType } from '~/types/licenses/licenses';

const store = useSubscribeStore();
const { license, selectedPlanType, purchaseType, selectedTerm } = storeToRefs(useSubscribeStore()); 
const { formatCurrency } = useUtils();
const personStore = usePersonStore();

// Compute available licenses for the selected plan type
const availableLicenses = computed(() => {
    if (purchaseType.value !== PurchaseType.GIFT) return 0;
    
    const availableLicenses = personStore.person?.licenses.filter(license => {
        return license.slug === LicenseType.SIGNATURE && 
               license.term === selectedTerm.value && 
               license.expired === false && 
               !license.assigned_to;
    });
    
    return availableLicenses?.length || 0;
});

const isMonthlyPlan = computed(() => selectedTerm.value === LicenseRenewalTerm.MONTHLY);

const selectPlan = () => {
    if (!isMonthlyPlan.value) {
        selectedPlanType.value = LicenseType.SIGNATURE;
    }
};

const priceParts = computed(() => {
    const formatted = formatCurrency(store.getCostPerLicenseAfterAnnualDiscount(LicenseType.SIGNATURE));
    const match = formatted.match(/^(.*?)[.,](\d{2})$/);
    if (match) {
        return { dollars: match[1], cents: match[2] };
    }
    return { dollars: formatted, cents: null };
});
</script>

<style lang="scss" scoped>
.signature-plan {
    @apply flex justify-between items-start sm:px-8 sm:py-6 p-4 self-stretch border;
    @apply border-zinc-300 cursor-pointer transition-all duration-300 ease-in-out;
    @apply flex-col lg:flex-row gap-4 lg:gap-0;

    &:hover {
        @apply -translate-y-[2px] bg-blue-10;
    }

    .left-section-sig {
        @apply flex-col;

        p {
            @apply text-zinc-600 text-sm font-normal mt-1 leading-6;
        }

        .left-section-header {
            @apply flex items-center gap-3;

            .license-title-container {
                @apply flex flex-col gap-2 flex-1;

                .license-name-price-container {
                    @apply flex items-center justify-between w-full;

                    span:first-child {
                        @apply text-zinc-900 font-inter text-2xl font-semibold leading-8;
                    }

                    .price-text {
                        @apply text-zinc-900 font-inter text-2xl font-semibold leading-8 text-end;
                    }

                    .user-time-text {
                        @apply text-zinc-600 font-inter text-sm font-normal leading-6 text-end;
                    }
                }

                .available-licenses-badge {
                    @apply bg-violet-500 text-white text-xs font-medium ml-2 px-2 py-1 rounded-full inline-flex items-center justify-center min-w-0 w-auto flex-shrink-0;
                }

                .current-license-badge {
                    @apply bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full inline-flex items-center justify-center min-w-0 w-auto flex-shrink-0;
                }
            }
        }
    }

    .right-section-sig {
        @apply grid justify-items-start lg:justify-items-end sm:w-52 w-full;

        span {
            @apply text-zinc-900 font-inter text-2xl font-semibold leading-8 text-end;
        }

        .price-text {
            @apply text-zinc-900 font-inter text-2xl font-semibold leading-8 text-end;
        }

        .user-time-text {
            @apply text-zinc-600 font-inter text-sm font-normal leading-6 text-end;
        }
    }
}

.benefits {
    @apply flex flex-col items-start gap-6 px-8 pt-8 pb-9 self-stretch border border-zinc-300;
}
.benefits-list {
    @apply flex flex-col items-start gap-y-4 self-stretch;
    @apply lg:flex-row lg:flex-wrap lg:gap-x-4;
}
.benefit {
    @apply flex items-start gap-3 flex-1;

    p {
        @apply text-zinc-600 font-inter text-sm font-normal leading-6 w-full lg:w-60;
    }
}
.check-icon {
    @apply w-6 h-6 text-green-500;
}

.disabled {
    opacity: 0.6;
    cursor: not-allowed;
    &:hover {
        background: inherit;
        transform: none;
    }
}
.cents-sup {
    @apply text-xs align-super leading-none relative -top-1;
}
</style>
